import { Tab, TabPane } from '@flowus/common/components';
import { message } from '@flowus/common/components/message';
import { toDayjsDateFormat } from '@flowus/common/i18n';
import { useOpenModal } from '@flowus/common/next-modal';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { ViewPath } from '@flowus/common/static-const';
import { exportCsv } from '@flowus/common/utils/export-file';
import { Modals, SpacePlanType } from '@flowus/shared';
import type { SharePagePayRecord, WithdrawOrderDTO } from '@next-space/fe-api-idl';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Icon } from 'src/common/components/icon';
import { Tooltip } from 'src/common/components/tooltip';
import { HOST_NAME } from 'src/common/const';
import { request } from 'src/common/request';
import { downloadUrlFile } from 'src/common/utils/download-utils';
import { DefaultPage } from 'src/editor/editor/raw/default-page';
import { useOpenPage } from 'src/hooks/page/use-open-page';
import { usePermissions } from 'src/hooks/share/use-permissions';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { useIsTeamProSpace } from 'src/hooks/space/use-is-pro-space';
import { getUserName } from 'src/hooks/user/use-remark-name';
import { blocksActions } from 'src/redux/reducers/blocks';
import { usersActions } from 'src/redux/reducers/users';
import { cache, dispatch } from 'src/redux/store';
import type { NextBlock } from 'src/redux/types';
import { getCurrencySymbols } from 'src/utils/currency-format';
import { usePickBlock } from 'src/utils/pick-block';
import { SegmentPlainText } from '../../aside/toc/helper';
import { ProIcon } from '../../header/pro-icon';
import { useOpenUpgradeModal } from '../upgrade-setting/upgrade-setting-header';
import { WelfareCodeList } from './welfare-code-list';

interface Props {
  defaultTabIndex?: number;
}
export const IncomeRecord: FC<Props> = (props) => {
  const { t } = useAppTranslation();
  const openModal = useOpenModal();
  return (
    <Tab
      active={props.defaultTabIndex ?? 0}
      className="w-[70vw] px-4 h-[500px]"
      childrenClassName="h-[calc(100%-50px)]"
      customButtons={() => (
        <Icon
          name="IcClose"
          size="middle"
          className="animate-click"
          onClick={() => openModal.closeModal(Modals.INCOME_RECORD)}
        />
      )}
    >
      <TabPane title={t('views.settings.income.income_details')}>
        <IncomeList />
      </TabPane>
      <TabPane title={t('common.withdrawal_records')}>
        <CashWithdrawList />
      </TabPane>
      {!__BUILD_IN__ && (
        <TabPane title={t('common.my_redemption_codes')}>
          <WelfareCodeList />
        </TabPane>
      )}
    </Tab>
  );
};

const CashWithdrawList: FC = () => {
  const { t } = useAppTranslation();
  const dateTimeFormat = `${toDayjsDateFormat(t('common.date_format_ymd_no_time'))} HH:mm`;
  const [orderList, setOrderList] = useState<WithdrawOrderDTO[]>();
  useEffect(() => {
    // 每天只允许提现一次，所以loadmore就先不做了吧)
    void request.infra.getWithdrawOrderList(1, 365, getCurrentSpaceId()).then((res) => {
      setOrderList(res.list);
    });
  }, []);
  if (orderList === undefined) return null;
  const getPayStatusDesc = (status?: number) => {
    // 状态 0 可提现, 1 已提现, 2 已取消
    switch (status) {
      case 0: {
        return t('common.withdrawable');
      }
      case 1: {
        return t('views.settings.income.withdrawn');
      }
      default: {
        return t('common.cancelled');
      }
    }
  };
  return (
    <div className="h-full px-1 flex flex-col justify-between items-center overflow-y-hidden overscroll-x-auto flex-1">
      <div className="grid grid-cols-10 gap-x-2 w-full py-2.5 text-t2 border-b">
        <div className="col-span-3">{t('views.settings.income.record_content')}</div>
        <div className="col-span-1">{t('common.status')}</div>
        <div className="col-span-1">{t('common.amount')}</div>
        <div className="col-span-3">{t('views.settings.income.receiving_account')}</div>
        <div className="col-span-2">{t('common.created_at')}</div>
      </div>
      <div className="overflow-y-auto w-full h-full">
        {orderList.map((item, index) => {
          item.total;
          return (
            <div className="grid grid-cols-10 gap-x-2 w-full py-2.5 text-t2" key={index}>
              <div className="col-span-3 break-words">
                {item.description ?? t('common.no_data')}
              </div>
              <div className="break-words col-span-1">
                <div>{getPayStatusDesc(item.status)}</div>
              </div>
              <div className="break-words col-span-1">
                {/* TODO 需要后台支持 */}
                {getCurrencySymbols(undefined)}
                {item.total}
              </div>
              <Tooltip className="col-span-3" popup={getUserName(item.userId)}>
                <div className="text-ellipsis">
                  {`${getUserName(item.userId)}(${cache.users[item.userId ?? '']?.phone})`}
                </div>
              </Tooltip>
              <div className="break-words col-span-2">
                {item.createdAt ? dayjs(new Date(item.createdAt)).format(dateTimeFormat) : ''}
              </div>
            </div>
          );
        })}
        {orderList.length === 0 && (
          <div className="text-t4 text-center text-grey3 mt-3">
            {t('views.settings.income.no_withdrawal_records')}
          </div>
        )}
      </div>
    </div>
  );
};
const IncomeList: FC = () => {
  const { t } = useAppTranslation();
  const dateTimeFormat = `${toDayjsDateFormat(t('common.date_format_ymd_no_time'))} HH:mm`;
  const dateTimeSecondFormat = `${toDayjsDateFormat(t('common.date_format_ymd_no_time'))} HH:mm:ss`;
  const isTeamSpace = useIsTeamProSpace('', true);
  const [payRecord, setPayRecord] = useState<SharePagePayRecord[]>();
  const [hasMore, setHasMore] = useState<boolean>(false);
  // const openSettingModal = useOpenSettingModal()
  const openUpgradeModal = useOpenUpgradeModal();

  const load = async (index = 1) => {
    const maxSize = 200;
    const res = await request.infra.getSharePaidRecords(getCurrentSpaceId(), index, maxSize);
    res.recordMap.blocks &&
      dispatch(
        blocksActions.update({
          blocks: res.recordMap.blocks as Record<string, NextBlock>,
        })
      );
    res.recordMap.users && dispatch(usersActions.updateSpaceUsers({ users: res.recordMap.users }));

    return {
      isMore: maxSize <= res.results.length,
      results: res.results,
    };
  };

  useEffect(() => {
    void load().then((res) => {
      setPayRecord(res.results);
      setHasMore(res.isMore);
    });
  }, []);

  // 转换数据为 CSV 格式
  const convertToCSV = (data: SharePagePayRecord[]) => {
    // CSV 表头
    const headers = [
      t('views.settings.income.page_id'),
      t('views.settings.income.user'),
      t('common.phone'),
      t('common.created_at'),
      t('common.amount'),
      t('common.validity_period'),
    ];

    // 转换数据行
    const csvRows = data.map((item) => {
      return [
        `${HOST_NAME}${ViewPath.share}${item.blockId}`,
        getUserName(item.userId),
        cache.users[item.userId ?? '']?.phone,
        dayjs(item.createdAt).format(dateTimeSecondFormat),
        item.total,
        (item.payValidDays || 0) <= 0 ? t('common.permanent_valid') : item.payValidDays,
      ].map((value) => `${value}`);
    });

    // 组合表头和数据行
    const res = [headers, ...csvRows];
    const url = exportCsv(res);

    void downloadUrlFile(
      url,
      `subscribe-${dayjs().format('YYYY-MM-DD_HH:mm')}.csv`.replaceAll(':', '_')
    );
  };

  const exportData = async () => {
    if (!isTeamSpace) {
      openUpgradeModal(SpacePlanType.team);
      return;
    }
    if (payRecord) {
      const loading = message.loading(t('common.exporting_data'));
      let more = true;
      let index = 2;
      const _payRecord = [...payRecord];
      while (more) {
        const res = await load(index);
        if (res.isMore) {
          more = true;
          _payRecord.push(...res.results);
          index++;
        } else {
          _payRecord.push(...res.results);
          more = false;
        }
      }

      message.closeMessage(loading);
      convertToCSV(_payRecord);
    } else {
      message.error(t('common.no_content'));
    }
  };

  const exportRecentData = async () => {
    if (!isTeamSpace) {
      openUpgradeModal(SpacePlanType.team);
      return;
    }
    if (payRecord) {
      const loading = message.loading(t('common.exporting_data'));
      let more = true;
      let index = 2;
      const _payRecord = [...payRecord];
      const maxRecords = 300;

      while (more && _payRecord.length < maxRecords) {
        const res = await load(index);
        _payRecord.push(...res.results);
        if (!res.isMore || _payRecord.length >= maxRecords) {
          more = false;
        } else {
          index++;
        }
      }

      message.closeMessage(loading);
      // 只导出前300条
      convertToCSV(_payRecord.slice(0, maxRecords));
    } else {
      message.error(t('common.no_content'));
    }
  };

  if (payRecord === undefined) return null;
  const getPayStatusDesc = (status?: number) => {
    // 状态 0 未付款, 1 已支付, 2 已取消,3 已提现
    switch (status) {
      case 0: {
        return t('common.unpaid');
      }
      case 2: {
        return t('common.cancelled');
      }
      default: {
        return t('common.paid');
      }
    }
  };
  return (
    <div className="h-full px-1 flex flex-col justify-between items-center overflow-y-hidden overscroll-x-auto flex-1">
      <div className="grid grid-cols-10 gap-x-2 w-full py-2.5 text-t2 border-b">
        <div className="col-span-3">{t('common.page_source')}</div>
        <div className="col-span-1">{t('views.settings.income.type')}</div>
        <div className="col-span-1">{t('common.amount')}</div>
        <div className="col-span-3">{t('views.settings.income.payer')}</div>
        <div className="col-span-2">{t('common.created_at')}</div>
        {/* <div className="col-span-1">操作</div> */}
      </div>
      <div className="overflow-y-auto w-full h-full">
        {payRecord.map((item, index) => {
          item.blockId;
          return (
            <div className="grid grid-cols-10 gap-x-2 w-full py-2.5 text-t2" key={index}>
              <div className="col-span-3 break-words">
                <Page id={item.blockId ?? ''} />
              </div>
              <div className="break-words col-span-1">
                <div>{getPayStatusDesc(item.status)}</div>
              </div>
              <div className="break-words col-span-1">
                {/* TODO 需要后台支持 */}
                {getCurrencySymbols(undefined)}
                {item.total}
              </div>
              <Tooltip className="col-span-3" popup={getUserName(item.userId)}>
                <div className="text-ellipsis">
                  {`${getUserName(item.userId)}(${cache.users[item.userId ?? '']?.phone})`}
                </div>
              </Tooltip>
              <div className="break-words col-span-2">
                {item.createdAt ? dayjs(new Date(item.createdAt)).format(dateTimeFormat) : ''}
              </div>
            </div>
          );
        })}
        {payRecord.length === 0 && (
          <div className="text-t4 text-center text-grey3 mt-3">
            {t('views.settings.income.no_income_details')}
          </div>
        )}
      </div>
      <div
        className={
          hasMore
            ? 'flex items-center justify-center gap-4 py-2.5'
            : 'py-2.5 flex items-center justify-center'
        }
      >
        {hasMore ? (
          <>
            <div
              className="text-active_color animate-click text-t2 flex items-center space-x-2"
              onClick={exportData}
            >
              <span>{t('common.export_all_data')}</span>
              {!isTeamSpace && <ProIcon absolute={false} showIcon text={t('common.team_plan')} />}
            </div>
            <div
              className="text-active_color animate-click text-t2 flex items-center space-x-2"
              onClick={exportRecentData}
            >
              <span>{t('common.export_recent_300_records')}</span>
              {!isTeamSpace && <ProIcon absolute={false} showIcon text={t('common.team_plan')} />}
            </div>
          </>
        ) : (
          <div
            className="text-active_color animate-click text-t2 flex items-center space-x-2"
            onClick={exportData}
          >
            <span>{t('common.export_data')}</span>
            {!isTeamSpace && <ProIcon absolute={false} showIcon text={t('common.team_plan')} />}
          </div>
        )}
      </div>
    </div>
  );
};

const Page: FC<{ id: string }> = (props) => {
  const { t } = useAppTranslation();
  const { illegal } = usePermissions(props.id);
  const block = usePickBlock(props.id, ['data'], ['segments']);
  const title = !block ? t('common.no_access') : <SegmentPlainText uuid={props.id ?? ''} />;
  const openPage = useOpenPage();
  const handlePageClick = () => {
    openPage(props.id, {
      forceOpenNewTab: true,
    });
  };
  return <DefaultPage id={props.id} title={title} illegal={illegal} onClick={handlePageClick} />;
};
